import React from 'react';

export interface BadgeProps {
  variant?: 'success' | 'error' | 'info' | 'warning' | 'neutral';
  size?: 'sm' | 'md';
  dot?: boolean;
  pulse?: boolean;
  children: React.ReactNode;
  className?: string;
}

const variantClasses: Record<NonNullable<BadgeProps['variant']>, string> = {
  success: 'bg-green-500/12 text-green-400 border border-green-500/20',
  error: 'bg-red-500/12 text-red-400 border border-red-500/20',
  info: 'bg-blue-500/12 text-blue-400 border border-blue-500/20',
  warning: 'bg-amber-500/12 text-amber-400 border border-amber-500/20',
  neutral: 'bg-surface-3 text-text-tertiary border border-border-1',
};

const dotColors: Record<NonNullable<BadgeProps['variant']>, string> = {
  success: 'bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.6)]',
  error: 'bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.6)]',
  info: 'bg-blue-400 shadow-[0_0_6px_rgba(96,165,250,0.6)]',
  warning: 'bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.6)]',
  neutral: 'bg-text-quaternary',
};

const Badge: React.FC<BadgeProps> = ({
  variant = 'neutral',
  size = 'sm',
  dot = false,
  pulse = false,
  children,
  className = '',
}) => {
  return (
    <span
      className={`
        inline-flex items-center gap-1.5
        font-semibold rounded-badge
        ${size === 'sm' ? 'px-2.5 py-[3px] text-[11px]' : 'px-3 py-1 text-[12px]'}
        ${variantClasses[variant]}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {dot && (
        <span className={`
          w-1.5 h-1.5 rounded-full flex-shrink-0
          ${dotColors[variant]}
          ${pulse ? 'animate-pulse' : ''}
        `.trim().replace(/\s+/g, ' ')} />
      )}
      {children}
    </span>
  );
};

export default Badge;
